import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { QueryClientProvider } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import PageTransition from './components/PageTransition';
import LoadingSpinner from './components/LoadingSpinner';
import Onboarding from './components/Onboarding';
import { AnalyticsProvider } from './components/AnalyticsProvider';
import FeedbackWidget from './components/FeedbackWidget';
import { AlienEasterEgg } from './components/easter-eggs';
import { queryClient } from './config/queryClient';
import { authService } from './services/auth';
import { settingsService } from './services/settings';
import api from './services/api';
import ErrorBoundary from './components/ErrorBoundary';
import SectionErrorBoundary from './components/SectionErrorBoundary';
import './App.css';

// Lazy load with automatic retry — handles transient network failures and stale chunk references
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyWithRetry(importFn: () => Promise<{ default: React.ComponentType<any> }>) {
  return lazy(() =>
    importFn().catch(() => {
      // Retry once after a short delay (covers transient network blips)
      return new Promise<{ default: React.ComponentType<any> }>((resolve, reject) => {
        setTimeout(() => importFn().then(resolve).catch(reject), 1500);
      });
    })
  );
}

// Lazy load components for code splitting (with retry for deploy resilience)
const Dashboard = lazyWithRetry(() => import('./components/Dashboard'));
const TasksEnhanced = lazyWithRetry(() => import('./components/tasks/TasksEnhanced'));
const Checklists = lazyWithRetry(() => import('./components/Checklists'));
const CalendarEnhanced = lazyWithRetry(() => import('./components/calendar/CalendarEnhanced'));
const RoutinesEnhanced = lazyWithRetry(() => import('./components/RoutinesEnhanced'));
const Journal = lazyWithRetry(() => import('./components/Journal'));
const HealthOverview = lazyWithRetry(() => import('./components/health/HealthOverview'));
const HealthJournal = lazyWithRetry(() => import('./components/health/HealthJournal'));
const HealthMealPlanning = lazyWithRetry(() => import('./components/health/HealthMealPlanning'));
const HealthExercise = lazyWithRetry(() => import('./components/health/HealthExercise'));
const HealthMeasurements = lazyWithRetry(() => import('./components/health/HealthMeasurements'));
const VisionGoalsProjects = lazyWithRetry(() => import('./components/VisionGoalsProjects'));
const Settings = lazyWithRetry(() => import('./components/Settings'));
const Integrations = lazyWithRetry(() => import('./components/IntegrationsSection'));
const ProfilePage = lazyWithRetry(() => import('./components/ProfileModal'));
const AdminPage = lazyWithRetry(() => import('./pages/AdminPage'));
const AdminAnalyticsDashboard = lazyWithRetry(() => import('./pages/AdminAnalyticsDashboard'));
const FeedbackDashboard = lazyWithRetry(() => import('./pages/FeedbackDashboard'));
const SupportTicketDashboard = lazyWithRetry(() => import('./pages/SupportTicketDashboard'));
const EmailTemplateDashboard = lazyWithRetry(() => import('./pages/EmailTemplateDashboard'));
const KnowledgeBaseDashboard = lazyWithRetry(() => import('./pages/KnowledgeBaseDashboard'));
const HelpCenter = lazyWithRetry(() => import('./pages/HelpCenter'));
const AnalyticsDashboard = lazyWithRetry(() => import('./pages/AnalyticsDashboard'));
const UsersPage = lazyWithRetry(() => import('./pages/UsersPage'));
const ActivityPage = lazyWithRetry(() => import('./pages/ActivityPage'));
const CMSPage = lazyWithRetry(() => import('./pages/CMSPage'));
const LandingPage = lazyWithRetry(() => import('./pages/LandingPage'));
const SignupPage = lazyWithRetry(() => import('./pages/SignupPage'));
const LoginPage = lazyWithRetry(() => import('./pages/LoginPage'));
const ResetPasswordPage = lazyWithRetry(() => import('./pages/ResetPasswordPage'));
const ResetPasswordConfirmPage = lazyWithRetry(() => import('./pages/ResetPasswordConfirmPage'));
const GoogleDebugPage = lazyWithRetry(() => import('./pages/GoogleDebugPage'));
const DocsPage = lazyWithRetry(() => import('./pages/DocsPage'));
const ChangelogPage = lazyWithRetry(() => import('./pages/ChangelogPage'));
const RoadmapPage = lazyWithRetry(() => import('./pages/RoadmapPage'));
const AboutPage = lazyWithRetry(() => import('./pages/AboutPage'));
const BlogPage = lazyWithRetry(() => import('./pages/BlogPage'));
const CareersPage = lazyWithRetry(() => import('./pages/CareersPage'));
const ContactPage = lazyWithRetry(() => import('./pages/ContactPage'));
const TermsPage = lazyWithRetry(() => import('./pages/TermsPage'));
const Analytics = lazyWithRetry(() => import('./components/Analytics'));
const PrivacyPage = lazyWithRetry(() => import('./pages/PrivacyPage'));
const SecurityPage = lazyWithRetry(() => import('./pages/SecurityPage'));
const PaymentSuccess = lazyWithRetry(() => import('./pages/PaymentSuccess'));
const PaymentCancelled = lazyWithRetry(() => import('./pages/PaymentCancelled'));
const HelpPage = lazyWithRetry(() => import('./pages/HelpPage'));
const FeedbackPage = lazyWithRetry(() => import('./pages/FeedbackPage'));
const HowToGuidePage = lazyWithRetry(() => import('./pages/HowToGuidePage'));
const OAuthCallbackPage = lazyWithRetry(() => import('./pages/OAuthCallbackPage'));
// Budget OS components
const BudgetIncome = lazyWithRetry(() => import('./components/budget-os/IncomeEnhanced'));
const BudgetExpenses = lazyWithRetry(() => import('./components/budget-os/ExpenseEnhanced'));
const Budget = lazyWithRetry(() => import('./components/budget-os/BudgetDashboard'));
const BudgetSavings = lazyWithRetry(() => import('./components/budget-os/SavingsEnhanced'));
const InvestmentJournal = lazyWithRetry(() => import('./components/budget-os/InvestmentJournal'));
const BudgetTransactions = lazyWithRetry(() => import('./components/budget-os/Transactions'));
const BudgetCategories = lazyWithRetry(() => import('./components/budget-os/Categories'));
const BudgetReserves = lazyWithRetry(() => import('./components/budget-os/Reserves'));
const BudgetReports = lazyWithRetry(() => import('./components/budget-os/Reports'));

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState('');
  const [authInitialized, setAuthInitialized] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(() => {
    // Initialize with correct state based on screen width
    return typeof window !== 'undefined' && window.innerWidth > 768;
  });
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);

  // Handle window resize to auto-open/close sidebar when crossing the mobile/desktop breakpoint
  useEffect(() => {
    let wasMobile = window.innerWidth <= 768;

    const handleResize = () => {
      const isMobile = window.innerWidth <= 768;

      // Only update when crossing the breakpoint threshold
      if (isMobile !== wasMobile) {
        wasMobile = isMobile;
        setIsSidebarOpen(!isMobile);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close sidebar on mobile after login
  useEffect(() => {
    if (isLoggedIn && window.innerWidth <= 768) {
      setIsSidebarOpen(false);
    }
  }, [isLoggedIn]);

  // Initialize authentication and CSRF token on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Fetch CSRF token first (needed for authenticated requests)
        try {
          await api.get('/csrf-token');
        } catch (error) {
          // CSRF token fetch failure is non-critical, continue with auth
          console.error('Failed to fetch CSRF token:', error);
        }

        const user = await authService.initialize();
        if (user) {
          setIsLoggedIn(true);
          setUserName(user.displayName || '');

          // Check onboarding status
          try {
            const onboardingCompleted = await settingsService.getOnboardingStatus();
            setShowOnboarding(!onboardingCompleted);
          } catch (error) {
            console.error('Error checking onboarding status:', error);
            // Default to showing onboarding if check fails
            setShowOnboarding(true);
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        setAuthInitialized(true);
        setOnboardingChecked(true);
      }
    };

    initAuth();

    // Listen for auth state changes (e.g., from Google OAuth)
    const handleAuthChange = () => {
      const user = authService.getCurrentUser();
      if (user) {
        setIsLoggedIn(true);
        setUserName(user.displayName || '');
      }
    };
    window.addEventListener('auth-state-changed', handleAuthChange);
    return () => window.removeEventListener('auth-state-changed', handleAuthChange);
  }, []);

  const handleLogin = () => {
    setIsLoggedIn(true);
  };

  const handleLogout = async () => {
    await authService.logout();
    setIsLoggedIn(false);
    setUserName('');
    setShowOnboarding(false);
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  const handleOnboardingSkip = () => {
    setShowOnboarding(false);
  };

  // Inner component that can use React Router hooks
  const AppContent: React.FC<{
    isLoggedIn: boolean;
    userName: string;
    onLogin: () => void;
    onLogout: () => void;
    isSidebarOpen: boolean;
    setIsSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
    showOnboarding: boolean;
    onOnboardingComplete: () => void;
    onOnboardingSkip: () => void;
  }> = ({ isLoggedIn, userName, onLogin, onLogout, isSidebarOpen, setIsSidebarOpen, showOnboarding, onOnboardingComplete, onOnboardingSkip }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [activeView, setActiveView] = useState('dashboard');
    const [easterEggsEnabled, setEasterEggsEnabled] = useState(() => {
      return localStorage.getItem('alienEasterEggsEnabled') !== 'false';
    });

    // Listen for easter egg setting changes from Settings component
    useEffect(() => {
      const handleEasterEggChange = (e: CustomEvent<boolean>) => {
        setEasterEggsEnabled(e.detail);
      };
      window.addEventListener('easterEggSettingChanged', handleEasterEggChange as EventListener);
      return () => {
        window.removeEventListener('easterEggSettingChanged', handleEasterEggChange as EventListener);
      };
    }, []);

    // Map URL path to viewId
    const getViewIdFromPath = (pathname: string): string => {
      let path = pathname.replace('/app', '').replace(/^\//, '');
      if (!path || path === '') {
        path = 'dashboard';
      }
      return path;
    };

    // Sync activeView with URL on mount and location change
    useEffect(() => {
      const viewId = getViewIdFromPath(location.pathname);
      setActiveView(viewId);
    }, [location.pathname]);

    const handleNavigate = (viewId: string) => {
      const newPath = `/app/${viewId}`;
      navigate(newPath);
      // Close sidebar on mobile after navigation
      if (window.innerWidth <= 768) {
        setIsSidebarOpen(false);
      }
    };

    const renderContent = () => {
      const SuspenseWrapper: React.FC<{ children: React.ReactElement }> = ({ children }) => (
        <Suspense fallback={<LoadingSpinner size="large" message="Loading..." />}>
          <ErrorBoundary>{children}</ErrorBoundary>
        </Suspense>
      );

      switch (activeView) {
        case 'dashboard':
          return (
            <SuspenseWrapper>
              <Dashboard userName={userName} />
            </SuspenseWrapper>
          );
        case 'tasks':
          return (
            <SuspenseWrapper>
              <TasksEnhanced />
            </SuspenseWrapper>
          );
        case 'checklists':
          return (
            <SuspenseWrapper>
              <Checklists />
            </SuspenseWrapper>
          );
        case 'calendar':
          return (
            <SuspenseWrapper>
              <CalendarEnhanced />
            </SuspenseWrapper>
          );
        case 'routines':
          return (
            <SuspenseWrapper>
              <RoutinesEnhanced />
            </SuspenseWrapper>
          );
        case 'journal':
          return (
            <SuspenseWrapper>
              <Journal />
            </SuspenseWrapper>
          );
        case 'health':
          // Redirect to health-overview for backward compatibility
          return <Navigate to="/app/health-overview" replace />;
        case 'health-overview':
          return (
            <SuspenseWrapper>
              <HealthOverview />
            </SuspenseWrapper>
          );
        case 'health-journal':
          return (
            <SuspenseWrapper>
              <HealthJournal />
            </SuspenseWrapper>
          );
        case 'health-meal-planning':
          return (
            <SuspenseWrapper>
              <HealthMealPlanning />
            </SuspenseWrapper>
          );
        case 'health-exercise':
          return (
            <SuspenseWrapper>
              <HealthExercise />
            </SuspenseWrapper>
          );
        case 'health-measurements':
          return (
            <SuspenseWrapper>
              <HealthMeasurements />
            </SuspenseWrapper>
          );
        case 'goals':
          return (
            <SuspenseWrapper>
              <VisionGoalsProjects />
            </SuspenseWrapper>
          );
        case 'analytics':
          return (
            <SuspenseWrapper>
              <Analytics />
            </SuspenseWrapper>
          );
        case 'integrations':
          return (
            <SuspenseWrapper>
              <Integrations />
            </SuspenseWrapper>
          );
        case 'profile':
          return (
            <SuspenseWrapper>
              <ProfilePage />
            </SuspenseWrapper>
          );
        case 'settings':
          return (
            <SuspenseWrapper>
              <Settings />
            </SuspenseWrapper>
          );
        case 'admin':
          return (
            <AdminRoute>
              <SuspenseWrapper>
                <AdminPage />
              </SuspenseWrapper>
            </AdminRoute>
          );
        case 'admin-analytics':
          return (
            <AdminRoute>
              <SuspenseWrapper>
                <AdminAnalyticsDashboard />
              </SuspenseWrapper>
            </AdminRoute>
          );
        case 'feedback-dashboard':
          return (
            <AdminRoute>
              <SuspenseWrapper>
                <FeedbackDashboard />
              </SuspenseWrapper>
            </AdminRoute>
          );
        case 'support-tickets':
          return (
            <AdminRoute>
              <SuspenseWrapper>
                <SupportTicketDashboard />
              </SuspenseWrapper>
            </AdminRoute>
          );
        case 'email-templates':
          return (
            <AdminRoute>
              <SuspenseWrapper>
                <EmailTemplateDashboard />
              </SuspenseWrapper>
            </AdminRoute>
          );
        case 'knowledge-base':
          return (
            <AdminRoute>
              <SuspenseWrapper>
                <KnowledgeBaseDashboard />
              </SuspenseWrapper>
            </AdminRoute>
          );
        case 'help-center':
          return (
            <SuspenseWrapper>
              <HelpCenter />
            </SuspenseWrapper>
          );
        case 'analytics-dashboard':
          return (
            <AdminRoute>
              <SuspenseWrapper>
                <AnalyticsDashboard />
              </SuspenseWrapper>
            </AdminRoute>
          );
        case 'admin-users':
          return (
            <AdminRoute>
              <SuspenseWrapper>
                <UsersPage />
              </SuspenseWrapper>
            </AdminRoute>
          );
        case 'admin-activity':
          return (
            <AdminRoute>
              <SuspenseWrapper>
                <ActivityPage />
              </SuspenseWrapper>
            </AdminRoute>
          );
        case 'admin-cms':
          return (
            <AdminRoute>
              <SuspenseWrapper>
                <CMSPage />
              </SuspenseWrapper>
            </AdminRoute>
          );
        // Budget OS routes
        case 'budget-os-income':
          return (
            <SuspenseWrapper>
              <BudgetIncome />
            </SuspenseWrapper>
          );
        case 'budget-os-expenses':
          return (
            <SuspenseWrapper>
              <BudgetExpenses />
            </SuspenseWrapper>
          );
        case 'budget-os-budget':
          return (
            <SuspenseWrapper>
              <Budget />
            </SuspenseWrapper>
          );
        case 'budget-os-savings':
          return (
            <SuspenseWrapper>
              <BudgetSavings />
            </SuspenseWrapper>
          );
        case 'budget-os-investment-journal':
          return (
            <SuspenseWrapper>
              <InvestmentJournal />
            </SuspenseWrapper>
          );
        case 'budget-os-transactions':
          return (
            <SuspenseWrapper>
              <BudgetTransactions />
            </SuspenseWrapper>
          );
        case 'budget-os-categories':
          return (
            <SuspenseWrapper>
              <BudgetCategories />
            </SuspenseWrapper>
          );
        case 'budget-os-reserves':
          return (
            <SuspenseWrapper>
              <BudgetReserves />
            </SuspenseWrapper>
          );
        case 'budget-os-reports':
          return (
            <SuspenseWrapper>
              <BudgetReports />
            </SuspenseWrapper>
          );
        default:
          return (
            <SuspenseWrapper>
              <Dashboard userName={userName} />
            </SuspenseWrapper>
          );
      }
    };

    return (
      <>
        {showOnboarding && (
          <Onboarding
            onComplete={onOnboardingComplete}
            onSkip={onOnboardingSkip}
          />
        )}
        <Header
          isLoggedIn={isLoggedIn}
          userName={userName}
          onLogin={onLogin}
          onLogout={onLogout}
          onNavigate={handleNavigate}
          onToggleSidebar={() => setIsSidebarOpen(prev => !prev)}
        />
        <div className="app-layout">
          <Sidebar
            activeItem={activeView}
            onNavigate={handleNavigate}
            isOpen={isSidebarOpen}
            onClose={() => setIsSidebarOpen(false)}
          />
          <main className="main-content">
            <AnimatePresence mode="wait">
              <PageTransition key={activeView}>
                {renderContent()}
              </PageTransition>
            </AnimatePresence>
          </main>
        </div>
        {/* Easter Egg: Cute aliens appear when idle */}
        <AlienEasterEgg enabled={easterEggsEnabled} idleThreshold={30000} />
      </>
    );
  };


  const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
    // Wait for auth to initialize
    if (!authInitialized) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#0a0e27' }}>
          <p style={{ color: '#34d399' }}>Loading...</p>
        </div>
      );
    }
    if (!isLoggedIn) {
      return <Navigate to="/login" replace />;
    }
    return children;
  };

  // Admin Route Component
  const AdminRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
    // Wait for auth to initialize
    if (!authInitialized) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#0a0e27' }}>
          <LoadingSpinner size="large" />
        </div>
      );
    }

    if (!isLoggedIn) {
      return <Navigate to="/login" replace />;
    }

    const user = authService.getCurrentUser();
    if (!user?.isAdmin) {
      return (
        <div style={{ padding: 'var(--spacing-xl)', textAlign: 'center' }}>
          <h2>Access Denied</h2>
          <p>You need admin privileges to access this page.</p>
        </div>
      );
    }

    return children;
  };

  // Public Only Route Component (redirect to app if already logged in)
  const PublicRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
    // Wait for auth to initialize
    if (!authInitialized) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#0a0e27' }}>
          <p style={{ color: '#34d399' }}>Loading...</p>
        </div>
      );
    }
    if (isLoggedIn) {
      return <Navigate to="/app" replace />;
    }
    return children;
  };

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AnalyticsProvider>
          <Router>
            <div className="App">
              <Routes>
                {/* Public routes */}
                <Route path="/" element={
                  <PublicRoute>
                    <Suspense fallback={<LoadingSpinner size="large" />}>
                      <ErrorBoundary>
                        <LandingPage />
                      </ErrorBoundary>
                    </Suspense>
                  </PublicRoute>
                } />

                <Route path="/signup" element={
                  <PublicRoute>
                    <Suspense fallback={<LoadingSpinner size="large" />}>
                      <ErrorBoundary>
                        <SignupPage />
                      </ErrorBoundary>
                    </Suspense>
                  </PublicRoute>
                } />

                <Route path="/login" element={
                  <PublicRoute>
                    <Suspense fallback={<LoadingSpinner size="large" />}>
                      <ErrorBoundary>
                        <LoginPage />
                      </ErrorBoundary>
                    </Suspense>
                  </PublicRoute>
                } />

                <Route path="/google-debug" element={
                  <Suspense fallback={<LoadingSpinner size="large" />}>
                    <ErrorBoundary>
                      <GoogleDebugPage />
                    </ErrorBoundary>
                  </Suspense>
                } />

                <Route path="/reset-password" element={
                  <PublicRoute>
                    <Suspense fallback={<LoadingSpinner size="large" />}>
                      <ErrorBoundary>
                        <ResetPasswordPage />
                      </ErrorBoundary>
                    </Suspense>
                  </PublicRoute>
                } />

                <Route path="/reset-password-confirm" element={
                  <PublicRoute>
                    <Suspense fallback={<LoadingSpinner size="large" />}>
                      <ErrorBoundary>
                        <ResetPasswordConfirmPage />
                      </ErrorBoundary>
                    </Suspense>
                  </PublicRoute>
                } />

                {/* Info pages - accessible to everyone */}
                <Route path="/docs" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <ErrorBoundary>
                      <DocsPage />
                    </ErrorBoundary>
                  </Suspense>
                } />
                <Route path="/changelog" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <ErrorBoundary>
                      <ChangelogPage />
                    </ErrorBoundary>
                  </Suspense>
                } />
                <Route path="/roadmap" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <ErrorBoundary>
                      <RoadmapPage />
                    </ErrorBoundary>
                  </Suspense>
                } />
                <Route path="/about" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <ErrorBoundary>
                      <AboutPage />
                    </ErrorBoundary>
                  </Suspense>
                } />
                <Route path="/blog" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <ErrorBoundary>
                      <BlogPage />
                    </ErrorBoundary>
                  </Suspense>
                } />
                <Route path="/careers" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <ErrorBoundary>
                      <CareersPage />
                    </ErrorBoundary>
                  </Suspense>
                } />
                <Route path="/contact" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <ErrorBoundary>
                      <ContactPage />
                    </ErrorBoundary>
                  </Suspense>
                } />
                <Route path="/terms" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <ErrorBoundary>
                      <TermsPage />
                    </ErrorBoundary>
                  </Suspense>
                } />
                <Route path="/privacy" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <ErrorBoundary>
                      <PrivacyPage />
                    </ErrorBoundary>
                  </Suspense>
                } />
                <Route path="/security" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <ErrorBoundary>
                      <SecurityPage />
                    </ErrorBoundary>
                  </Suspense>
                } />
                <Route path="/help" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <ErrorBoundary>
                      <HelpPage />
                    </ErrorBoundary>
                  </Suspense>
                } />
                <Route path="/feedback" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <ErrorBoundary>
                      <FeedbackPage />
                    </ErrorBoundary>
                  </Suspense>
                } />
                <Route path="/how-to-guide" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <ErrorBoundary>
                      <HowToGuidePage />
                    </ErrorBoundary>
                  </Suspense>
                } />

                {/* OAuth callback (popup receives Google redirect) */}
                <Route path="/oauth/callback" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <ErrorBoundary>
                      <OAuthCallbackPage />
                    </ErrorBoundary>
                  </Suspense>
                } />

                {/* Payment pages */}
                <Route path="/payment/success" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <ErrorBoundary>
                      <PaymentSuccess />
                    </ErrorBoundary>
                  </Suspense>
                } />
                <Route path="/payment/cancelled" element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <ErrorBoundary>
                      <PaymentCancelled />
                    </ErrorBoundary>
                  </Suspense>
                } />

                {/* Protected app routes */}
                <Route path="/app" element={
                  <ProtectedRoute>
                    <Navigate to="/app/dashboard" replace />
                  </ProtectedRoute>
                } />
                <Route path="/app/*" element={
                  <ProtectedRoute>
                    <AppContent
                      isLoggedIn={isLoggedIn}
                      userName={userName}
                      onLogin={handleLogin}
                      onLogout={handleLogout}
                      isSidebarOpen={isSidebarOpen}
                      setIsSidebarOpen={setIsSidebarOpen}
                      showOnboarding={showOnboarding}
                      onOnboardingComplete={handleOnboardingComplete}
                      onOnboardingSkip={handleOnboardingSkip}
                    />
                  </ProtectedRoute>
                } />

                {/* Fallback redirect */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="dark"
              />
            </div>
          </Router>
        </AnalyticsProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
